/**
 * Land Signal — per-job hireability score + 4-tier traffic-light bucket.
 *
 * Where `score.ts` answers "is this job RELEVANT to the user?", this
 * scorer answers "what is the user's REALISTIC CHANCE of LANDING this
 * job?". Different question, different weights.
 *
 * Pure functions only — no I/O, no DB, no AI. Same constraint as
 * `score.ts`. The orchestrator in `feed.ts` is responsible for fetching
 * jobs and merging optional LLM cohort overrides.
 *
 * Total score ceiling = 100 pts (v2 — rebalanced 2026-07-22, see
 * docs/plans/seeker-value-audit-2026-07-21.md T7):
 *   seniority barrier   12  (entry/junior easier to land than senior+)
 *   skill simplicity    20  (fewer required skills = lower barrier)
 *   competition         15  (fewer applicants relative to age = rare find)
 *   freshness           12  (recent post = employer still hiring)
 *   indonesia fit       12  (proxy for visa/region friction)
 *   salary clarity      13  (listed pay = serious employer)
 *   salary magnitude    15  (actual pay level, via {@link scoreSalaryAttractiveness})
 *   enrichment quality   1  (detailed post = real opportunity; minimal weight)
 *
 * v1 gave seniority barrier 30/100 on its own — enough to push ANY
 * intern/junior post into "lock-in" regardless of pay, so an unpaid
 * internship could outrank a salaried mid-level match (audit finding:
 * seniority 30 vs salary-clarity 10, no pay-MAGNITUDE axis at all). v2
 * cuts seniority to 12 and folds pay magnitude — not just whether pay is
 * listed — into the ceiling via {@link scoreSalaryMagnitude}, a thin
 * weighted wrapper over {@link scoreSalaryAttractiveness} (previously
 * feed-ordering-only). A dedicated hard penalty
 * ({@link scoreUnpaidInternPenalty}, -{@link UNPAID_INTERN_PENALTY_WEIGHT}
 * pts) additionally fires whenever a job is BOTH intern-tier AND lists no
 * pay at all — the exact combination that produced the bias, as opposed
 * to a merely low-paying-but-listed intern role.
 *
 * Authenticated users with profile signals get an additive bonus capped
 * at 20 pts to nudge the same job up when the user is genuinely a fit.
 * Total is still clamped to 100.
 *
 * The 0-100 score is bucketed into 4 tiers via {@link scoreToTier}.
 */

import type { ScoreBreakdown, UserSignals } from "./score";
import { seniorityTiersForYears } from "./score";

export const LAND_SIGNAL_VERSION = "v2";

/**
 * The visual tier exposed in the UI. Each tier maps to one colour pill
 * (blue / green / yellow / red) and one i18n label. Order matters: the
 * array index is sometimes used as a sort key.
 */
export const LAND_SIGNAL_TIERS = [
	"long-shot",
	"stretch",
	"strong-fit",
	"lock-in",
] as const;

export type LandSignalTier = (typeof LAND_SIGNAL_TIERS)[number];

export type LandSignalSource = "deterministic" | "llm" | "blend";

export type LandSignalInput = {
	seniority: string | null;
	coreSkillsCount: number;
	postedAt: Date | null;
	indonesiaFitScore: number | null;
	payMin: number | null;
	payMax: number | null;
	enrichmentCompleteness: number | null;
	/** Real-world demand signal for this job — sourced by the feed
	 * orchestrator (`feed.ts`) from `jobs.applyClicks` (outbound `/out` apply
	 * clicks, `NOT NULL DEFAULT 0`), not the opt-in application tracker: the
	 * tracker requires a seeker to manually log their own application, so it
	 * was ≈always null in practice and the competition axis never actually
	 * fired outside a handful of rows (2026-07-22 audit). `applyClicks`
	 * increments on every real outbound click, giving near-universal
	 * coverage. Null means the count was unavailable (treated as neutral,
	 * e.g. call sites that don't pass it at all). Zero means no one has
	 * clicked apply yet (low competition). */
	applicantCount?: number | null;
	/** Currency/period for the salary-magnitude axis — same optional,
	 * default-to-USD/yearly contract as {@link SalaryAttractivenessInput}.
	 * Omit only when genuinely unknown; an IDR salary read as USD would
	 * massively over-score the magnitude axis. */
	payCurrency?: string | null;
	payPeriod?: string | null;
};

export type LandSignalBreakdown = {
	seniorityBarrier: number;
	skillSimplicity: number;
	freshness: number;
	indonesiaFit: number;
	salaryClarity: number;
	salaryMagnitude: number;
	enrichmentQuality: number;
	competition: number;
	personalizationBonus: number;
	/** Negative (or 0). Fires only for intern-tier + zero listed pay — see
	 * {@link scoreUnpaidInternPenalty}. */
	unpaidInternPenalty: number;
};

export type LandSignal = {
	score: number;
	tier: LandSignalTier;
	source: LandSignalSource;
	breakdown: LandSignalBreakdown;
};

const SENIORITY_WEIGHT = 12;
const SKILL_SIMPLICITY_WEIGHT = 20;
const COMPETITION_WEIGHT = 15;
const FRESHNESS_WEIGHT = 12;
const FIT_WEIGHT = 12;
const SALARY_CLARITY_WEIGHT = 13;
const SALARY_MAGNITUDE_WEIGHT = 15;
const ENRICHMENT_WEIGHT = 1;
const MAX_PERSONALIZATION_BONUS = 20;
/** Hard penalty when a job is intern-tier AND lists no pay at all — see
 * {@link scoreUnpaidInternPenalty}. Not part of the 100-pt axis sum above
 * (it only ever subtracts); sized so an otherwise-maxed unpaid intern
 * post (seniority + skills + freshness + fit + competition all full
 * credit, salary axes at 0) can't clear a typical salaried mid-level
 * match on landability alone. */
const UNPAID_INTERN_PENALTY_WEIGHT = 15;

const ENTRY_TIERS = new Set(["intern", "junior", "entry"]);
const INTERN_TIER = "intern";
const MID_TIERS = new Set(["mid"]);
const SENIOR_TIERS = new Set(["senior", "staff", "lead", "principal"]);

/**
 * Lower seniority = lower barrier. An unknown seniority defaults to
 * half-credit so we don't over-penalise jobs the enricher couldn't tag.
 */
export function scoreSeniorityBarrier(seniority: string | null): number {
	if (!seniority) return Math.round(SENIORITY_WEIGHT * 0.5);
	const normalized = seniority.trim().toLowerCase();
	if (ENTRY_TIERS.has(normalized)) return SENIORITY_WEIGHT;
	if (MID_TIERS.has(normalized)) return Math.round(SENIORITY_WEIGHT * 0.5);
	if (SENIOR_TIERS.has(normalized)) return Math.round(SENIORITY_WEIGHT * 0.15);
	// "executive" or anything else → effectively 0
	return 0;
}

/**
 * Fewer required skills = easier application. Capped bands so a job with
 * 0 skills (often a stub) doesn't score the same as a clean 1-2 skills
 * post.
 */
export function scoreSkillSimplicity(coreSkillsCount: number): number {
	if (coreSkillsCount <= 0) return Math.round(SKILL_SIMPLICITY_WEIGHT * 0.5);
	if (coreSkillsCount <= 2) return SKILL_SIMPLICITY_WEIGHT;
	if (coreSkillsCount <= 5) return Math.round(SKILL_SIMPLICITY_WEIGHT * 0.7);
	if (coreSkillsCount <= 8) return Math.round(SKILL_SIMPLICITY_WEIGHT * 0.4);
	return Math.round(SKILL_SIMPLICITY_WEIGHT * 0.15);
}

/**
 * Recently posted = employer still actively hiring. Default `now` arg
 * makes the function testable without mocking Date.
 */
export function scoreFreshness(
	postedAt: Date | null,
	now: Date = new Date(),
): number {
	if (!postedAt) return 0;
	const ageMs = now.getTime() - postedAt.getTime();
	if (!Number.isFinite(ageMs) || ageMs < 0) return 0;
	const ageDays = ageMs / 86_400_000;
	if (ageDays <= 3) return FRESHNESS_WEIGHT;
	if (ageDays <= 7) return Math.round(FRESHNESS_WEIGHT * 0.75);
	if (ageDays <= 14) return Math.round(FRESHNESS_WEIGHT * 0.5);
	if (ageDays <= 30) return Math.round(FRESHNESS_WEIGHT * 0.25);
	return 0;
}

export function scoreIndonesiaFit(score: number | null): number {
	if (typeof score !== "number" || !Number.isFinite(score)) return 0;
	const clamped = Math.max(0, Math.min(1, score));
	return Math.round(FIT_WEIGHT * clamped);
}

export function scoreSalaryClarity(
	payMin: number | null,
	payMax: number | null,
): number {
	const hasMin = typeof payMin === "number" && payMin > 0;
	const hasMax = typeof payMax === "number" && payMax > 0;
	if (hasMin && hasMax) return SALARY_CLARITY_WEIGHT;
	if (hasMin || hasMax) return Math.round(SALARY_CLARITY_WEIGHT * 0.5);
	return 0;
}

/**
 * Salary ATTRACTIVENESS (value / pay magnitude). Feeds two consumers:
 * (1) {@link scoreSalaryMagnitude}, the weighted axis folded into
 * {@link computeLandSignal} since v2 — a well-paid role should land ABOVE an
 * equivalent unpaid one, not the same; and (2) the feed orchestrator's
 * separate hero-ordering balance of landability vs. value. Kept as a
 * standalone [0,1] normalizer (rather than inlined) so both call sites share
 * one FX/log-scale definition of "attractive pay".
 *
 * Returns a normalized value in [0, 1]:
 *   - 0 when no pay is listed (presence-safe — never throws).
 *   - currency-aware: converts to an approximate annual USD figure via a static
 *     FX/period table (comparison-only — never for display or billing).
 *   - log-scaled + saturating so $400k > $150k > $40k but the curve flattens at
 *     the top (a $1M outlier can't dominate the ordering).
 */
export type SalaryAttractivenessInput = {
	payMin: number | null;
	payMax: number | null;
	payCurrency?: string | null;
	payPeriod?: string | null;
};

/** Rough static FX → USD, comparison-only. Unknown/absent currency → USD (1). */
const FX_TO_USD: Record<string, number> = {
	USD: 1,
	EUR: 1.08,
	GBP: 1.27,
	SGD: 0.74,
	AUD: 0.66,
	CAD: 0.73,
	MYR: 0.21,
	INR: 0.012,
	IDR: 0.000063,
	PHP: 0.017,
};

/** Period → annual multiplier. Unknown/absent → yearly (1). */
const PERIOD_TO_ANNUAL: Record<string, number> = {
	hourly: 2080,
	daily: 260,
	weekly: 52,
	monthly: 12,
	yearly: 1,
	annual: 1,
	annually: 1,
};

export function scoreSalaryAttractiveness(
	input: SalaryAttractivenessInput,
): number {
	const min =
		typeof input.payMin === "number" && input.payMin > 0 ? input.payMin : null;
	const max =
		typeof input.payMax === "number" && input.payMax > 0 ? input.payMax : null;
	if (min === null && max === null) return 0;
	// Prefer the upper band (upside signal); fall back to whichever exists.
	const magnitude = max ?? min ?? 0;
	if (magnitude <= 0) return 0;

	const period = (input.payPeriod ?? "yearly").trim().toLowerCase();
	const annualMultiplier = PERIOD_TO_ANNUAL[period] ?? 1;
	const currency = (input.payCurrency ?? "USD").trim().toUpperCase();
	const fx = FX_TO_USD[currency] ?? 1;
	const annualUsd = magnitude * annualMultiplier * fx;
	if (!Number.isFinite(annualUsd) || annualUsd <= 0) return 0;

	// Log-scaled saturation anchored so ~$10k → 0, ~$500k → ~1.
	// $40k → ~0.35, $150k → ~0.69, $400k → ~0.94.
	const normalized = Math.log10(annualUsd / 10_000) / Math.log10(50);
	return Math.max(0, Math.min(1, normalized));
}

/**
 * PostHog flag gating the value-aware hero/enriched-pool ordering. Default OFF
 * → legacy pure-landability order (a fresh entry role can outrank a high-value
 * senior role even after the v2 seniority/salary-magnitude rebalance above).
 * When ENABLED, hero ordering blends landability with salary attractiveness so
 * a well-paid role is not buried beneath a low-pay one. Sole switch — no
 * shadow constant. Shared by both hero-ordering consumers
 * (`feed.ts` → `getColdStartFeed`/personalized feed, and
 * `cold-start-hero.ts` → `pickColdStartHero`) since neither should carry the
 * other's unrelated import graph.
 */
export const HERO_VALUE_RERANK_FLAG = "hero-value-rerank";

/** Weight of salary attractiveness in the value-aware hero ordering blend. */
const HERO_VALUE_WEIGHT = 0.35;

/**
 * Combined hero-ordering value used as the ranking key. Legacy (flag OFF) =
 * pure land score, preserving the current user-visible order exactly. When the
 * value-rerank flag is ON, blend landability (0–100) with salary attractiveness
 * (0–1, scaled to 0–100) so high-value roles are not buried beneath low-pay
 * ones. Pure + exported for unit testing.
 */
export function heroOrderingScore(
	landScore: number,
	attractiveness: number,
	valueRerankEnabled: boolean,
): number {
	if (!valueRerankEnabled) return landScore;
	return (
		landScore * (1 - HERO_VALUE_WEIGHT) +
		attractiveness * 100 * HERO_VALUE_WEIGHT
	);
}

/**
 * Salary-magnitude axis for {@link computeLandSignal} (v2, weight
 * {@link SALARY_MAGNITUDE_WEIGHT}). Thin weighted wrapper over
 * {@link scoreSalaryAttractiveness} — same [0,1] curve, scaled onto this
 * axis's points so a genuinely well-paid role outranks an equivalent
 * unpaid/low-pay one instead of scoring identically on landability.
 */
export function scoreSalaryMagnitude(
	payMin: number | null,
	payMax: number | null,
	payCurrency?: string | null,
	payPeriod?: string | null,
): number {
	const attractiveness = scoreSalaryAttractiveness({
		payMin,
		payMax,
		payCurrency,
		payPeriod,
	});
	return Math.round(SALARY_MAGNITUDE_WEIGHT * attractiveness);
}

/**
 * Hard penalty for the specific bias this was written to close: an
 * intern-tier post that lists NO pay at all (not merely low pay — a paid
 * internship keeps its normal seniority/salary treatment). Returns the
 * points to SUBTRACT ({@link UNPAID_INTERN_PENALTY_WEIGHT} or 0), not a
 * negative number — callers apply the sign.
 */
export function scoreUnpaidInternPenalty(
	seniority: string | null,
	payMin: number | null,
	payMax: number | null,
): number {
	if (!seniority || seniority.trim().toLowerCase() !== INTERN_TIER) return 0;
	const hasMin = typeof payMin === "number" && payMin > 0;
	const hasMax = typeof payMax === "number" && payMax > 0;
	if (hasMin || hasMax) return 0;
	return UNPAID_INTERN_PENALTY_WEIGHT;
}

export function scoreEnrichmentQuality(completeness: number | null): number {
	if (typeof completeness !== "number" || !Number.isFinite(completeness))
		return 0;
	const clamped = Math.max(0, Math.min(1, completeness));
	return Math.round(ENRICHMENT_WEIGHT * clamped);
}

/**
 * Competition score — fewer platform applicants relative to job age means
 * the user has a better shot. Banded thresholds intentionally lean toward
 * rewarding very-early applicants and penalising crowded listings.
 *
 * - `null` count → neutral half-credit (data not available)
 * - 0 applicants, posted ≤7 days → full credit (prime low-competition window)
 * - 0 applicants, posted >7 days → high but not full (signals less interest)
 * - 1–2 applicants → near-full (still very low competition)
 * - 3–5 applicants → moderate
 * - 6–15 applicants → low-moderate
 * - 16–30 applicants → low
 * - 31+ applicants → 0 (crowded listing)
 */
export function scoreCompetition(
	applicantCount: number | null | undefined,
	postedAt: Date | null,
	now: Date = new Date(),
): number {
	// Unknown count → neutral (half credit) to avoid penalising jobs that just
	// haven't been tracked yet (most jobs in prod currently have count = null).
	if (applicantCount === null || applicantCount === undefined)
		return Math.round(COMPETITION_WEIGHT * 0.5);

	const ageDays = postedAt
		? Math.max(0, (now.getTime() - postedAt.getTime()) / 86_400_000)
		: null;

	if (applicantCount === 0) {
		// Young post with 0 applicants = rare find
		if (ageDays !== null && ageDays <= 7) return COMPETITION_WEIGHT;
		// Older post with 0 applicants — employer may not be active, give high but not full
		return Math.round(COMPETITION_WEIGHT * 0.87);
	}
	if (applicantCount <= 2) return Math.round(COMPETITION_WEIGHT * 0.87);
	if (applicantCount <= 5) return Math.round(COMPETITION_WEIGHT * 0.67);
	if (applicantCount <= 15) return Math.round(COMPETITION_WEIGHT * 0.4);
	if (applicantCount <= 30) return Math.round(COMPETITION_WEIGHT * 0.2);
	return 0;
}

/**
 * Additive bonus when the authenticated user's relevance score signals
 * a real fit. Capped so personalization can't override the underlying
 * barrier-based ranking — a senior-only job stays Long shot for a
 * junior even if the category matches perfectly.
 */
export function personalizationBonus(
	breakdown: ScoreBreakdown | null,
	jobSeniority: string | null,
	signals: UserSignals | null,
): number {
	if (!breakdown || !signals) return 0;
	// Scale from the v1 relevance scorer (0-100) → 0-20.
	const scaled = Math.round(
		(breakdown.total / 100) * MAX_PERSONALIZATION_BONUS,
	);
	let bonus = Math.min(scaled, MAX_PERSONALIZATION_BONUS);

	// Pull bonus down if the user is junior but the job is clearly senior
	// — relevance might match the keyword but they can't land it.
	if (
		jobSeniority &&
		signals.yearsExperience !== null &&
		signals.yearsExperience <= 2
	) {
		const tiers = seniorityTiersForYears(signals.yearsExperience);
		const normalized = jobSeniority.trim().toLowerCase();
		if (!tiers.includes(normalized) && SENIOR_TIERS.has(normalized)) {
			bonus = Math.round(bonus * 0.25);
		}
	}
	return bonus;
}

export type ComputeLandScoreOptions = {
	now?: Date;
	personalization?: {
		breakdown: ScoreBreakdown;
		signals: UserSignals;
	};
};

/**
 * Returns the Land Signal for a single job. Always returns a valid
 * signal — never throws, never returns null. Missing fields degrade
 * gracefully via the per-axis helpers above.
 */
export function computeLandSignal(
	input: LandSignalInput,
	options: ComputeLandScoreOptions = {},
): LandSignal {
	const now = options.now ?? new Date();
	const seniorityBarrier = scoreSeniorityBarrier(input.seniority);
	const skillSimplicity = scoreSkillSimplicity(input.coreSkillsCount);
	const freshness = scoreFreshness(input.postedAt, now);
	const fit = scoreIndonesiaFit(input.indonesiaFitScore);
	const salaryClarity = scoreSalaryClarity(input.payMin, input.payMax);
	const salaryMagnitude = scoreSalaryMagnitude(
		input.payMin,
		input.payMax,
		input.payCurrency,
		input.payPeriod,
	);
	const enrichmentQuality = scoreEnrichmentQuality(
		input.enrichmentCompleteness,
	);
	const competition = scoreCompetition(
		input.applicantCount,
		input.postedAt,
		now,
	);
	const unpaidInternPenalty = scoreUnpaidInternPenalty(
		input.seniority,
		input.payMin,
		input.payMax,
	);
	const bonus = options.personalization
		? personalizationBonus(
				options.personalization.breakdown,
				input.seniority,
				options.personalization.signals,
			)
		: 0;

	const raw =
		seniorityBarrier +
		skillSimplicity +
		freshness +
		fit +
		salaryClarity +
		salaryMagnitude +
		enrichmentQuality +
		competition +
		bonus -
		unpaidInternPenalty;
	const score = Math.max(0, Math.min(100, raw));

	return {
		score,
		tier: scoreToTier(score),
		source: "deterministic",
		breakdown: {
			seniorityBarrier,
			skillSimplicity,
			freshness,
			indonesiaFit: fit,
			salaryClarity,
			salaryMagnitude,
			enrichmentQuality,
			competition,
			personalizationBonus: bonus,
			// `|| 0` normalizes JS's `-0` (from `-unpaidInternPenalty` when the
			// penalty is 0) back to `+0` so strict equality checks don't trip.
			unpaidInternPenalty: -unpaidInternPenalty || 0,
		},
	};
}

/**
 * Bucket a 0-100 score into the 4-tier traffic light.
 *
 * Thresholds chosen so a typical "clean entry-level remote post with
 * salary listed in the last week" lands in the green band, and the
 * default "no signals at all" baseline lands in yellow.
 */
export function scoreToTier(score: number): LandSignalTier {
	if (score >= 80) return "lock-in";
	if (score >= 60) return "strong-fit";
	if (score >= 40) return "stretch";
	return "long-shot";
}

/**
 * Merge a deterministic signal with an LLM-issued tier override (from
 * the cold-start cohort cache). The LLM never moves the score number;
 * it only adjusts the tier when it has high-confidence evidence the
 * deterministic bucket is wrong. Result is marked `source: "blend"`.
 */
export function applyLlmTierOverride(
	signal: LandSignal,
	llmTier: LandSignalTier | null,
): LandSignal {
	if (!llmTier || llmTier === signal.tier) return signal;
	return {
		...signal,
		tier: llmTier,
		source: signal.source === "deterministic" ? "blend" : "llm",
	};
}

/**
 * True when the job shows a genuine low-competition signal: 0–2 platform
 * applicants (a known, non-null count) AND the job is not otherwise a
 * long-shot. Null applicantCount scores neutral half-credit (8 pts) —
 * well below the 13-pt threshold — so the badge never fires on coverage
 * gaps where we simply don't have data.
 *
 * Single source of truth for the hidden-gem badge in the feed, the picks
 * card, the job-detail sidebar, and the "Sepi pelamar" feed filter.
 * Never inline the threshold elsewhere.
 */
export function isHiddenGem(signal: LandSignal): boolean {
	// competition >= 13 maps to applicantCount = 0 (any post age, 13–15 pts)
	// or 1–2 applicants (13 pts). Neutral null-count = 8 pts → never fires.
	// Exclude long-shot: a generally poor-quality job is not a "gem" even if
	// it happens to have few applicants on our platform.
	return signal.breakdown.competition >= 13 && signal.tier !== "long-shot";
}

/**
 * The stricter gate the BADGE uses (the `feed-badge-row` v2 arm), as opposed
 * to the filter/sidebar predicate above.
 *
 * `isHiddenGem` matched 3,805 of 5,652 live jobs on 2026-09-07 — 67% of the
 * board. A "be the first to apply" claim that is true of two thirds of every
 * listing carries no information, and it rests on `jobs.apply_clicks`, our own
 * outbound-click counter, which is near-zero by default and is known to be
 * inflated by automation. Requiring competition >= 15 encodes exactly "zero
 * recorded applicants AND posted within 7 days" (see the competition axis
 * above), which matched 821 of 5,652 — 14.5%.
 *
 * Deliberately a second predicate rather than a moved threshold: `isHiddenGem`
 * still backs the "Sepi pelamar" feed filter, the picks card and the job-detail
 * sidebar, and a filter that matches 14.5% of inventory instead of 67% is a
 * different product from the one users have been using.
 */
export function isFirstMoverGem(signal: LandSignal): boolean {
	return signal.breakdown.competition >= 15 && signal.tier !== "long-shot";
}
